import { useEffect, useState, type FormEvent } from "react";
import { Marke } from "../ui/Marke";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, rufe } from "../api/client";
import type { LadenEinstellungen } from "@shared/schemas/laden";
import { BRANDING } from "./branding";
import { Button, Notice, Seitenzustand, TextField } from "../ui";
import { Card, FormGrid, PageHeader, Select } from "../ui/Layout";
import { useLaden, useStammdatenSpeichern, type LadenDaten } from "./api";
import { LadenStammdaten } from "@shared/schemas/laden";
import { ApiFehler } from "../api/client";

export function Einstellungen() {
  const laden = useLaden();
  return (
    <>
      <PageHeader kicker="Einstellungen" title="Laden" sub="Name, Adresse, Steuer – erscheint auf Abrechnungen und Rechnungen" />
      <Seitenzustand query={laden}>{(d) => <StammdatenFormular daten={d} />}</Seitenzustand>
      <Seitenzustand query={laden}>{(d) => <OeffnungszeitenUndBranding daten={d} />}</Seitenzustand>
    </>
  );
}

export function StammdatenFormular({ daten, onGespeichert, knopfText = "Speichern" }: { daten: LadenDaten; onGespeichert?: () => void; knopfText?: string }) {
  const speichern = useStammdatenSpeichern();
  const [form, setForm] = useState({
    name: daten.name,
    strasse: daten.adresse.strasse ?? "",
    plz: daten.adresse.plz ?? "",
    ort: daten.adresse.ort ?? "",
    telefon: daten.telefon,
    email: daten.email,
    steuernummer: daten.steuernummer,
    ustModus: daten.ustModus,
  });
  const [fehler, setFehler] = useState<Record<string, string>>({});
  const [erfolg, setErfolg] = useState(false);
  useEffect(() => {
    if (!erfolg) return;
    const t = setTimeout(() => setErfolg(false), 4000);
    return () => clearTimeout(t);
  }, [erfolg]);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler({});
    const parsed = LadenStammdaten.safeParse({
      name: form.name,
      adresse: { strasse: form.strasse, plz: form.plz, ort: form.ort },
      telefon: form.telefon,
      email: form.email,
      steuernummer: form.steuernummer,
      ustModus: form.ustModus,
    });
    if (!parsed.success) {
      const f: Record<string, string> = {};
      for (const issue of parsed.error.issues) f[String(issue.path[0] ?? "name")] = issue.message;
      setFehler(f);
      return;
    }
    try {
      await speichern.mutateAsync(parsed.data);
      setErfolg(true);
      onGespeichert?.();
    } catch (err) {
      setFehler({ _: err instanceof ApiFehler ? err.message : "Speichern hat nicht geklappt. Bitte noch einmal versuchen." });
    }
  }

  return (
    <form className="app-form" onSubmit={absenden} noValidate>
      {fehler._ ? <Notice tone="error" title="Nicht gespeichert">{fehler._}</Notice> : null}
      {erfolg ? <Notice tone="success" title="Gespeichert">Die Änderung steht im Änderungsprotokoll.</Notice> : null}
      <Card title="Laden">
        <FormGrid>
          <TextField label="Name des Ladens" value={form.name} onChange={set("name")} error={fehler.name} autoComplete="organization" />
          <TextField label="Telefon" value={form.telefon} onChange={set("telefon")} type="tel" autoComplete="tel" />
          <TextField label="E-Mail für Mieter" value={form.email} onChange={set("email")} type="email" error={fehler.email} />
        </FormGrid>
        <FormGrid>
          <TextField label="Straße und Hausnummer" value={form.strasse} onChange={set("strasse")} autoComplete="street-address" />
          <TextField label="PLZ" value={form.plz} onChange={set("plz")} inputMode="numeric" autoComplete="postal-code" />
          <TextField label="Ort" value={form.ort} onChange={set("ort")} autoComplete="address-level2" />
        </FormGrid>
      </Card>
      <Card title="Steuer">
        <FormGrid>
          <TextField label="Steuernummer" value={form.steuernummer} onChange={set("steuernummer")} hint="Erscheint auf Abrechnungen und Mietrechnungen" />
          <Select label="Umsatzsteuer" value={form.ustModus} onChange={set("ustModus")} hint="Gilt für Miete und Provision (Plan, Frage 9)">
            <option value="regel19">Regelbesteuerung, 19 % auf Miete und Provision</option>
            <option value="kleinunternehmer">Kleinunternehmer, ohne Umsatzsteuer (§ 19 UStG)</option>
          </Select>
        </FormGrid>
        <p className="app-help">Die Software ersetzt keine Steuerberatung.</p>
      </Card>
      <div className="app-form__actions">
        <Button type="submit" variant="primary" icon="check" loading={speichern.isPending}>
          {knopfText}
        </Button>
      </div>
    </form>
  );
}

const TAGE: [keyof LadenEinstellungen["oeffnungszeiten"], string][] = [["mo", "Montag"], ["di", "Dienstag"], ["mi", "Mittwoch"], ["do", "Donnerstag"], ["fr", "Freitag"], ["sa", "Samstag"], ["so", "Sonntag"]];

/** Kontrast nach WCAG (relative Leuchtdichte), für die Akzentfarbe. */
export function kontrast(hexA: string, hexB: string): number {
  const lum = (hex: string) => {
    const n = parseInt(hex.slice(1), 16);
    const k = [16, 8, 0].map((sh) => {
      const c = ((n >> sh) & 255) / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * k[0]! + 0.7152 * k[1]! + 0.0722 * k[2]!;
  };
  const [a, b] = [lum(hexA), lum(hexB)];
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}


/** Öffnungszeiten und Akzentfarbe (Logo-Upload kommt mit dem Foto-Upload in Etappe 4). */
function OeffnungszeitenUndBranding({ daten }: { daten: LadenDaten }) {
  const qc = useQueryClient();
  const e = daten.einstellungen;
  const [zeiten, setZeiten] = useState(e.oeffnungszeiten);
  const [schliess, setSchliess] = useState(e.schliesstage);
  const [neuerTag, setNeuerTag] = useState("");
  const [akzent, setAkzent] = useState(e.branding.akzent ?? "");
  const speichern = useMutation({ mutationFn: (neu: LadenEinstellungen) => rufe(api.api.laden.einstellungen.$put({ json: neu })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden"] }) });
  const [erfolg, setErfolg] = useState(false);
  useEffect(() => {
    if (!erfolg) return;
    const t = setTimeout(() => setErfolg(false), 4000);
    return () => clearTimeout(t);
  }, [erfolg]);
  const gueltig = /^#[0-9a-fA-F]{6}$/.test(akzent);
  const kHell = gueltig ? kontrast(akzent, BRANDING.hintergrundHell) : 0;
  const kWeiss = gueltig ? kontrast(akzent, BRANDING.weiss) : 0;
  const bestanden = gueltig && kHell >= 4.5 && kWeiss >= 4.5;

  function absenden(ev: FormEvent) {
    ev.preventDefault();
    const neu: LadenEinstellungen = { ...e, oeffnungszeiten: zeiten, schliesstage: { ...schliess, tage: [...new Set(schliess.tage)].sort() }, branding: { ...e.branding, akzent: akzent && bestanden ? akzent.toLowerCase() : null } };
    speichern.mutate(neu, { onSuccess: () => setErfolg(true) });
  }

  return (
    <form className="app-form" onSubmit={absenden} noValidate style={{ marginTop: "var(--space-5)" }}>
      {erfolg ? <Notice tone="success" title="Gespeichert">Öffnungszeiten und Akzentfarbe sind aktualisiert.</Notice> : null}
      <Card title="Öffnungszeiten">
        <p className="sc-muted">Stehen im Mieterportal und auf Hinweisen.</p>
        <div className="sc-col" style={{ gap: "var(--space-2)" }}>
          {TAGE.map(([k, name]) => {
            const z = zeiten[k];
            return (
              <div key={k} className="sc-row" style={{ flexWrap: "wrap" }}>
                <span style={{ width: 110 }}>{name}</span>
                <input type="time" className="sc-select" value={z?.von ?? ""} disabled={!z} onChange={(ev) => setZeiten({ ...zeiten, [k]: { von: ev.target.value, bis: z?.bis ?? "18:00" } })} aria-label={`${name} von`} />
                <span>bis</span>
                <input type="time" className="sc-select" value={z?.bis ?? ""} disabled={!z} onChange={(ev) => setZeiten({ ...zeiten, [k]: { von: z?.von ?? "10:00", bis: ev.target.value } })} aria-label={`${name} bis`} />
                <label className="sc-check"><input type="checkbox" checked={!z} onChange={(ev) => setZeiten({ ...zeiten, [k]: ev.target.checked ? null : { von: "10:00", bis: "18:00" } })} /> geschlossen</label>
              </div>
            );
          })}
        </div>
      </Card>
      <Card title="Schließtage">
        <p className="sc-muted">An Schließtagen gibt es keine Termine für Mieter; die Belegung markiert sie. Mietzeiten verschieben sich nicht.</p>
        <label className="sc-check"><input type="checkbox" checked={schliess.feiertageSH} onChange={(ev) => setSchliess({ ...schliess, feiertageSH: ev.target.checked })} /> Gesetzliche Feiertage Schleswig-Holstein automatisch (Neujahr, Karfreitag, Ostermontag, 1. Mai, Himmelfahrt, Pfingstmontag, 3. Oktober, Reformationstag, Weihnachten)</label>
        <div className="sc-toolbar">
          {schliess.tage.map((t) => <button key={t} type="button" className="sc-pill" aria-label={`${t} entfernen`} onClick={() => setSchliess({ ...schliess, tage: schliess.tage.filter((x) => x !== t) })}>{t.slice(8, 10)}.{t.slice(5, 7)}.{t.slice(0, 4)} ×</button>)}
          <input type="date" className="sc-select" aria-label="Schließtag hinzufügen" value={neuerTag} onChange={(ev) => setNeuerTag(ev.target.value)} />
          <Button variant="quiet" icon="plus" disabled={!neuerTag || schliess.tage.includes(neuerTag)} onClick={() => { setSchliess({ ...schliess, tage: [...schliess.tage, neuerTag] }); setNeuerTag(""); }}>Hinzufügen</Button>
        </div>
      </Card>
      <Card title="Branding">
        <p className="sc-muted">Ohne eigene Farbe zeigt die Software Violett. Statusfarben und Fokusring ändern sich nie.</p>
        <div className="sc-toolbar">
          {BRANDING.vorschlaege.map((f) => (
            <button key={f} type="button" className="sc-pill" aria-pressed={akzent.toLowerCase() === f} onClick={() => setAkzent(f)} aria-label={`Farbe ${f}`} style={{ background: f, borderColor: f, color: BRANDING.weiss, minWidth: 56 }}>
              {akzent.toLowerCase() === f ? "✓" : ""}
            </button>
          ))}
          <button type="button" className="sc-pill" aria-pressed={!akzent} onClick={() => setAkzent("")}>Standard (Violett)</button>
        </div>
        <FormGrid>
          <TextField label="Eigene Farbe (Hex)" value={akzent} onChange={(ev) => setAkzent(ev.target.value.trim())} placeholder={BRANDING.vorschlaege[0]} hint="Wird beim Speichern auf Lesbarkeit geprüft" />
        </FormGrid>
        {gueltig ? (
          <div className="sc-toolbar">
            <span>Farbe auf Hintergrund</span>
            <span className={`rg-badge ${kHell >= 4.5 ? "rg-badge--success" : "rg-badge--attention"}`}>{kHell.toFixed(1)} : 1 · {kHell >= 4.5 ? "bestanden" : "zu hell"}</span>
            <span>Weiße Schrift auf Farbe</span>
            <span className={`rg-badge ${kWeiss >= 4.5 ? "rg-badge--success" : "rg-badge--attention"}`}>{kWeiss.toFixed(1)} : 1 · {kWeiss >= 4.5 ? "bestanden" : "zu hell"}</span>
            {!bestanden ? <span className="sc-muted sc-small">Fällt die Farbe durch, bleibt Violett aktiv.</span> : null}
          </div>
        ) : akzent ? <p className="app-help">Bitte als Hex mit sechs Stellen, z. B. {BRANDING.vorschlaege[0]}.</p> : null}
      </Card>
      <div className="app-form__actions">
        <Button type="submit" variant="primary" icon="check" loading={speichern.isPending}>Speichern</Button>
      </div>
      <LogoKarte daten={daten} />
    </form>
  );
}

/** Logo (Etappe 7): PNG/JPEG/WebP bis 1 MB; erscheint in Verwaltung, Mieterportal und auf der Anmeldeseite. */
function LogoKarte({ daten }: { daten: LadenDaten }) {
  const qc = useQueryClient();
  const [fehler, setFehler] = useState<string | null>(null);
  const [stand, setStand] = useState(0);
  const hochladen = useMutation({
    mutationFn: async (datei: File) => {
      const res = await fetch("/api/laden/logo", { method: "PUT", headers: { "content-type": datei.type }, body: datei, credentials: "same-origin" });
      if (!res.ok) throw new ApiFehler(((await res.json().catch(() => ({}))) as { fehler?: string }).fehler ?? "Upload fehlgeschlagen.", res.status);
    },
    onSuccess: () => { setStand(Date.now()); setFehler(null); void qc.invalidateQueries({ queryKey: ["laden"] }); },
    onError: (e) => setFehler(e instanceof ApiFehler ? e.message : "Upload fehlgeschlagen."),
  });
  const entfernen = useMutation({ mutationFn: () => rufe(api.api.laden.logo.$delete()), onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden"] }) });
  const logo = daten.einstellungen.branding.logoKey;
  return (
    <Card title="Logo">
      <p className="sc-muted">PNG, JPEG oder WebP bis 1 MB, am besten quadratisch und mindestens 256 × 256 Pixel. Erscheint oben links in Verwaltung und Mieterportal sowie auf der Anmeldeseite.</p>
      {fehler ? <Notice tone="error">{fehler}</Notice> : null}
      <div className="sc-row" style={{ gap: "var(--space-4)", flexWrap: "wrap" }}>
        {logo ? <img src={`/api/public/laden/${daten.slug}/logo?v=${stand}`} alt="Logo" width={72} height={72} style={{ borderRadius: "var(--radius-md)", objectFit: "contain", background: "var(--surface)" }} /> : <Marke size={72} />}
        <label className="rg-btn rg-btn--secondary">
          {hochladen.isPending ? "Lädt …" : logo ? "Logo ersetzen" : "Logo hochladen"}
          <input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(ev) => { const f = ev.target.files?.[0]; if (f) hochladen.mutate(f); ev.target.value = ""; }} />
        </label>
        {logo ? <Button variant="quiet" icon="x" loading={entfernen.isPending} onClick={() => entfernen.mutate()}>Entfernen</Button> : null}
      </div>
    </Card>
  );
}
